import { parse } from 'papaparse';

/**
 * Availability/status.
 * - `"HOMES_CONNECTED"` = House is already connected to fibre optics
 * - `"HOMES_PASSED"` = Street is connected and house could be connected
 * - `null` = No connection possible
 */
type FibreAvailability = 'HOMES_CONNECTED' | 'HOMES_PASSED' | null;

/** Unknown - not needed */
type BER_NR = string;

/** Unknown - not needed */
type HAUPTBER_KEN = string;

/** combination of `${STRASSE} ${HAUSNUMMER}${HAUSNUMMER2}, ${BEZ}` – not needed */
type STO_NAME = string;

/** Street name */
type STRASSE = string;

/** House number (part 1/2) */
type HAUSNUMMER = string;

/** House number (part 2/2) */
type HAUSNUMMER2 = string;

/** Zip code */
type PLZ = string;

/** City name */
type BEZ = string;

/** Unknown - not needed */
type WE = string;

/** Availability */
type ART = FibreAvailability;

/** Unknown - not needed */
type KEN = string;

/**
 * This is how the data is stored in the CSV file. Availability has the index `9`.
 * Indexes we use:
 * - 3: Street name
 * - 4: House number part 1/2
 * - 5: House number part 2/2
 * - 6: Zip code
 * - 7: City name
 * - 9: Availability
 */
type RawRowData = [
  BER_NR,
  HAUPTBER_KEN,
  STO_NAME,
  STRASSE,
  HAUSNUMMER,
  HAUSNUMMER2,
  PLZ,
  BEZ,
  WE,
  ART,
  KEN
];

/**
 * An address existing in the real world. The {@link status} depends on the availability and condition of fiber optics.
 */
interface Address {
  /** Postal code */
  zip: string;

  /** City name */
  city: string;

  /** Street name */
  street: string;

  /** House number */
  houseNumber: string;

  /** The status of the address. Can be `null`. */
  status?: FibreAvailability;
}

interface Field {
  /** Input field */
  input: HTMLInputElement;

  /** Data list associated with the {@link input} */
  dataList: HTMLDataListElement;
}

/** Selector for any instance of this _Friendation_ component */
const selector = '[is="block-fibre-checker"]';

/** Data for the fibreChecker */
const addresses: Address[] = [];

/**
 * Friendation component
 */
export const blockFibreChecker = () => {
  /** Instances of this Friendation component */
  const components: HTMLElement[] = Array.from(
    document.querySelectorAll(selector)
  );

  components.forEach(async (component) => {
    /** The form containing all {@link fields} */
    const form = component.querySelector('form');

    /** Elements that contain a text message for the user. A message should be visible when the {@link form} is submitted. There are different messages for each value of {@link FibreAvailability}. */
    const messages: HTMLElement[] = Array.from(
      component.querySelectorAll('.message')
    );

    const submitButton: HTMLButtonElement =
      form.querySelector('[type="submit"]');

    /** Fields in {@link form}. We query the fields in {@link handleForm} */
    const fields: {
      zip: Field;
      street: Field;
      houseNumber: Field;
    } = {
      zip: {
        input: null,
        dataList: null
      },
      street: {
        input: null,
        dataList: null
      },
      houseNumber: {
        input: null,
        dataList: null
      }
    };

    /** Populate the zip code field with matching zip codes */
    const populateZipCodes = () => {
      /** Clear options in datalist element */
      fields.zip.dataList.innerHTML = '';

      /** Build options for datalist element */
      addresses
        .sort((a, b) => {
          const x = a.zip;
          const y = b.zip;

          return x.localeCompare(y);
        })
        .forEach((address) => {
          /** This is the value we add as an option. We also use this to test for duplicates. */
          const value = `${address.zip}, ${address.city}`;

          /** Avoid duplicates */
          if (
            Array.from(fields.zip.dataList.options).find(
              (x) => x.value === value
            )
          )
            return;

          const option = document.createElement('option');
          option.value = value;
          fields.zip.dataList.append(option);
        });
    };

    const populateStreets = (zipCode: Address['zip']) => {
      /** Clear options in datalist element */
      fields.street.dataList.innerHTML = '';

      /**
       * Build options for datalist element: find all street names, that are valid for the selected {@link zipCode}
       */
      addresses
        .sort((a, b) => {
          const x = a.street;
          const y = b.street;

          return x.localeCompare(y);
        })
        .filter((x) => x.zip === zipCode)
        .forEach((address) => {
          /** only add non existing values */
          if (
            Array.from(fields.street.dataList.options).find(
              (x) => x.value === address.street
            )
          )
            return;

          const option = document.createElement('option');
          option.value = address.street;
          fields.street.dataList.append(option);
        });
    };

    const populateHouseNumbers = (street: Address['street']) => {
      /** Clear options in datalist element */
      fields.houseNumber.dataList.innerHTML = '';

      /**
       * Build options for datalist element: find all street names, that are valid for the selected {@link zipCode}
       */
      addresses
        .sort((a, b) => parseInt(a.houseNumber) - parseInt(b.houseNumber))
        .filter((x) => x.street === street)
        .forEach((address) => {
          /** only add non existing values */
          if (
            Array.from(fields.houseNumber.dataList.options).find(
              (x) => x.value === address.houseNumber
            )
          )
            return;

          const option = document.createElement('option');
          option.value = address.houseNumber;
          fields.houseNumber.dataList.append(option);
        });
    };

    /**
     * User types in `<input name="zipCode">`
     */
    const onInputZip = () => {
      const value = fields.zip.input.value;

      /** Check if the input matches an item from {@link addresses} */
      const match = addresses.find(
        (x) =>
          `${x.zip}, ${x.city}`.toLowerCase() ===
          fields.zip.input.value.toLowerCase()
      );

      if (value.length && match) {
        fields.street.input.disabled = false;
        populateStreets(match.zip);
      } else {
        fields.street.input.value = '';
        fields.street.input.disabled = true;
        fields.houseNumber.input.value = '';
        fields.houseNumber.input.disabled = true;
      }
    };

    /**
     * User types in `<input name="street">`. When the input length is sufficient, we enable the house number field. We also populate the house number field with possible values, when the street is a valid street from {@link addresses}.
     */
    const onInputStreet = () => {
      const value = fields.street.input.value;

      /** Check if the input matches an item from {@link addresses} */
      const match = addresses.find(
        (x) =>
          x.street.toLowerCase() === fields.street.input.value.toLowerCase()
      );

      fields.houseNumber.input.disabled = !(value.length > 2);

      if (value.length > 2 && match) {
        populateHouseNumbers(match.street);
      } else {
        fields.houseNumber.input.value = '';
      }
    };

    /**
     * User types in `<input name="houseNumber">`
     */
    const onInputHouseNumber = () => {
      submitButton.disabled = !fields.houseNumber.input.value.length;
    };

    const handleForm = () => {
      fields.zip.input = form.querySelector('input[name="zipCode"]');
      fields.zip.dataList = form.querySelector(
        `[id="${fields.zip.input.getAttribute('list')}"]`
      );
      fields.street.input = form.querySelector('input[name="street"]');
      fields.street.dataList = form.querySelector(
        `[id="${fields.street.input.getAttribute('list')}"]`
      );
      fields.houseNumber.input = form.querySelector(
        'input[name="houseNumber"]'
      );
      fields.houseNumber.dataList = form.querySelector(
        `[id="${fields.houseNumber.input.getAttribute('list')}"]`
      );

      /** Bind event listeners to the input fields */
      fields.zip.input.addEventListener('input', onInputZip);
      fields.street.input.addEventListener('input', onInputStreet);
      fields.houseNumber.input.addEventListener('input', onInputHouseNumber);

      populateZipCodes();

      /** Control the submit event */
      form.addEventListener('submit', (e: SubmitEvent) => {
        e.preventDefault();
        submitData();
      });

      form.addEventListener('reset', () => {
        messages.forEach((message) => {
          message.inert = true;
        });

        fields.zip.input.focus();
      });
    };

    /**
     * Read and parse the data of a CSV file to be processed later. The CSV contains information about the availability of a fibre optic connection at an address.
     */
    const readData = async () => {
      if (addresses.length) return;

      /** URL to a CSV file on our server. File contains the required {@link addresses} */
      const linkToCsv = component.dataset.url;
      if (!linkToCsv)
        throw new Error(
          'Missing dataset attribute on component block-fibre-checker'
        );
      component.removeAttribute('data-url'); // remove this to pseudo-avoid exploitation

      const charset = 'utf-8';

      const myHeaders = new Headers();
      myHeaders.append('Content-Type', `text/plain; charset=${charset}`);

      /**
       * Get the contents of the uploaded CSV file UTF-8 encoded
       * @see {@link https://stackoverflow.com/questions/55282444/javascript-fetch-characters-with-encoding-issues}
       */
      const csv = await fetch(linkToCsv, { headers: myHeaders })
        .then((x) => {
          return x.arrayBuffer();
        })
        .then((x) => {
          const decoder = new TextDecoder(charset);
          const text = decoder.decode(x);
          return text;
        });

      const csvData = await parse(csv, {});

      /** The number of columns we expect each row from the {@link csvData} to have. We can verify results with with number. */
      const numberOfColums = 11;

      /** Parse the raw CSV data */
      const rows: RawRowData[] = <RawRowData[]>csvData.data;

      /** Invalid rows */
      const invalidRows = [];

      /**
       * Returns `true` if a {@link row} is a valid instance of {@link RawRowData}
       * @param row Row data
       * @param i Row index
       */
      const checkValidity = (row: RawRowData, i?: number) => {
        let valid = true;

        /** Make sure a row has a valid amount of columns */
        if (row.length !== numberOfColums) valid = false;

        /** Check for required data {@link Address.zip} and check if it is valid data */
        if (row[6] === undefined || typeof parseInt(row[6]) !== 'number')
          valid = false;

        /** Check required data that we need for {@link Address.city} */
        if (row[7] === undefined) valid = false;

        /** Check required data that we need for {@link Address.street} */
        if (row[3] === undefined) valid = false;

        /** Check required data that we need for {@link Address.houseNumber} */
        if (row[4] === undefined) valid = false;

        /** Collect invalid rows */
        if (!valid) invalidRows.push({ row, i });

        return valid;
      };

      /** Loop over each row and build an {@link Address} */
      if (rows && rows.length && rows[0].length === numberOfColums) {
        rows
          .filter((x) => x.length === numberOfColums)
          .forEach((row, i) => {
            /** First line contains the column titles and no data - we can skip it */
            if (i === 0) return;

            /** Check if a {@link row} is a valid instance of {@link RawRowData} */
            if (checkValidity(row, i)) {
              const zip: Address['zip'] = row[6].trim();

              let status: Address['status'] = null;
              if (row[9].trim() === 'HOMES_CONNECTED') {
                status = 'HOMES_CONNECTED';
              } else if (row[9].trim() === 'HOMES_PASSED') {
                status = 'HOMES_PASSED';
              }

              const city: Address['city'] = row[7].trim();
              const street: Address['street'] = row[3].trim();
              const houseNumber: Address['houseNumber'] = `${row[4].trim()}${row[5]?.trim()}`;

              /** Double check that the data is OK */
              if (
                zip.length &&
                city.length &&
                street.length &&
                houseNumber.length &&
                status !== undefined
              ) {
                /** Build a valid {@link Address} */
                const address: Address = {
                  zip,
                  city,
                  street,
                  houseNumber,
                  status
                };

                /** Add {@link address} to {@link addresses} */
                addresses.push(address);
              }
            }
          });
      } else {
        console.error(`blockFibreChecker: invalid data`, rows[1] || undefined);
      }

      if (invalidRows.length)
        console.warn(
          `blockFibreChecker: ${invalidRows.length} / ${rows.length} rows are invalid`,
          invalidRows
        );
    };

    /**
     * When data is submitted we send a message. We have a different message for each possible value of {@link FibreAvailability}.
     */
    const submitData = () => {
      /** Find an exact match for the current input. */
      const match: Address | undefined = addresses.find(
        (x) =>
          `${x.zip}, ${x.city}`.toLowerCase() ===
            fields.zip.input.value.toLowerCase().trim() &&
          x.street.toLowerCase() ===
            fields.street.input.value.toLowerCase().trim() &&
          x.houseNumber.toLowerCase() ===
            fields.houseNumber.input.value.toLowerCase().trim()
      );

      /** One of {@link messages}. We display the message, that is associated with the current {@link match.status}. */
      const chosenMessage = messages.find((x) => {
        if (match && match.status) {
          return x.dataset.value.trim() === match.status;
        }

        return x.dataset.value.trim() === '';
      });

      /** Hide all possible messages */
      messages.forEach((message) => {
        message.inert = true;
      });

      /** Show and focus the chosen message */
      chosenMessage.inert = false;
      chosenMessage.tabIndex = -1;
      setTimeout(() => {
        chosenMessage.focus();
      }, 100);
    };

    readData().then(() => {
      handleForm();
    });
  });
};
